// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
// --- External UI Libraries
import { Row, Col, Divider } from "antd";
import Typography from "@dtci/spellbook/Typography";
// --- Components
import { DetailRow } from "tap/components/DetailRow";
import { ItemCard } from "tap/components/ItemCard";
import { CollapsibleSection } from "tap/components/CollapsibleSection";
import { SectionHeader } from "tap/components/SectionHeader";
// --- Styles
import "./styles.scss";

// Status colors
const STATUS_COLORS = {
  green: "#4caf50",
  orange: "#ffa500",
  red: "#f44336",
  gray: "#9e9e9e",
};

// Status to color and icon mapping for Rate Cards
const getStatusConfig = (status) => {
  switch (status) {
    case "ACTIVE":
      return { icon: "fa-check", color: STATUS_COLORS.green };
    case "INACTIVE":
      return { icon: "fa-xmark", color: STATUS_COLORS.red };
    case "NEEDS_ATTENTION":
      return { icon: "fa-exclamation", color: STATUS_COLORS.orange };
    default:
      return { icon: null, color: STATUS_COLORS.gray };
  }
};

// Simple divider line
const SectionDivider = () => <div className="section-divider" />;

function RateCardDetails({ data }) {
  if (!data) return null;

  const statusConfig = getStatusConfig(data.status);

  // Helper: get window sequence
  const windowSequence = data.windowSequence || {};

  // Helper: get windows (sorted by windowOrder if available)
  const windows = Array.isArray(data.windows)
    ? [...data.windows].sort(
        (a, b) => (a.window?.windowOrder ?? 0) - (b.window?.windowOrder ?? 0),
      )
    : [];

  // Helper: flatten tiers by window, alias, and tier
  let flatTiers = [];
  if (Array.isArray(data.tierAliases)) {
    data.tierAliases.forEach((alias) => {
      if (Array.isArray(alias.tiers)) {
        alias.tiers.forEach((tier) => {
          let windowName = "";
          if (tier.id && tier.id.tapWindowId && Array.isArray(data.windows)) {
            const win = data.windows.find(
              (w) => w.id && w.id.tapWindowId === tier.id.tapWindowId,
            );
            windowName = win && win.window ? win.window.windowName : "";
          }
          flatTiers.push({
            aliasName: alias.tierAliasName,
            aliasFormat: alias.tierAliasFormat,
            windowName,
            tierName: tier.tierName,
            price: tier.price,
          });
        });
      }
    });
  }

  return (
    <div className="rate-card-details">
      {/* Rate Card Title & ID with Status Badge */}
      <div className="rate-card-details-title-block">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div className="rate-card-details-id">Rate Card #{data.id}</div>
          {/* Status Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 16,
              backgroundColor: statusConfig.color + "20",
              border: `1px solid ${statusConfig.color}40`,
            }}
          >
            {statusConfig.icon && (
              <i
                className={`fas ${statusConfig.icon}`}
                style={{ fontSize: 12, color: statusConfig.color }}
              />
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: statusConfig.color,
                textTransform: "uppercase",
              }}
            >
              {data.status}
            </span>
          </div>
        </div>
        <Typography variant="T40" bold className="rate-card-details-name">
          {data.rateCardName || "Untitled Rate Card"}
        </Typography>
      </div>

      {/* Key Details */}
      <div className="rate-card-details-key-section">
        <DetailRow label="TPR Rate Card" value={data.isTpr ? "Yes" : "No"} />
        <DetailRow label="Currency" value={data.currency} />
        <DetailRow label="Price Type" value={data.priceType} />
        <DetailRow label="Rev Share Type" value={data.revShareType} noBorder />
      </div>

      {/* Window Sequence */}
      {data.windowSequence && (
        <>
          <SectionHeader title="Window Sequence" />
          <div className="rate-card-details-window-section">
            <DetailRow label="Name" value={windowSequence.windowSequenceName} />
            <DetailRow label="Status" value={windowSequence.status} noBorder />
          </div>
          <SectionDivider />
        </>
      )}

      {/* Windows */}
      {windows.length > 0 && (
        <>
          <CollapsibleSection
            title="Windows"
            count={windows.length}
            defaultOpen={true}
          >
            {windows.map(
              (windowObj, idx) =>
                windowObj.window && (
                  <ItemCard key={idx}>
                    <DetailRow
                      label="Name"
                      value={windowObj.window.windowName}
                    />
                    <DetailRow
                      label="Duration"
                      value={windowObj.window.windowDuration}
                    />
                    <DetailRow
                      label="Duration Unit"
                      value={windowObj.window.windowDurationUnit}
                    />
                    {windowObj.percentage !== undefined &&
                      windowObj.percentage !== null && (
                        <DetailRow
                          label="Percentage"
                          value={`${windowObj.percentage}%`}
                          noBorder
                        />
                      )}
                  </ItemCard>
                ),
            )}
          </CollapsibleSection>
          <SectionDivider />
        </>
      )}

      {/* Tiers -> Prices */}
      {flatTiers.length > 0 && (
        <CollapsibleSection
          title="Tiers / Prices"
          count={flatTiers.length}
          defaultOpen={true}
        >
          {flatTiers.map((tier, idx) => (
            <ItemCard key={idx}>
              <DetailRow label="Alias Name" value={tier.aliasName} />
              <DetailRow label="Format" value={tier.aliasFormat} />
              <DetailRow label="Window" value={tier.windowName} />
              <DetailRow label="Tier" value={tier.tierName} />
              <DetailRow label="Price" value={tier.price} noBorder />
            </ItemCard>
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
}

export function ViewRateCardForm() {
  const { selectedRateCard, selectedHistory } = useSelector(
    (state) => state.rateCards,
  );

  if (!selectedRateCard) return null;

  const showHistory = selectedHistory && selectedHistory.data;

  return (
    <Row gutter={32} align="top" className="rate-card-form-row">
      <Col span={showHistory ? 11 : 24} className="rate-card-form-column">
        <RateCardDetails data={selectedRateCard} />
      </Col>
      {showHistory && (
        <>
          <Col span={2} className="rate-card-form-divider-column">
            <Divider type="vertical" className="rate-card-form-divider" />
          </Col>
          <Col span={11} className="rate-card-form-column">
            <RateCardDetails data={selectedHistory.data} />
          </Col>
        </>
      )}
    </Row>
  );
}
